import {
  IApiOptions,
  CreateErpDocumentResponse,
  IErpDocumentListRequestBody,
  IErpDocumentListResponse,
  IErpDocumentProductsRequestBody,
  IErpDocumentProductsResponse,
  IErpDocumentSyncRequestBody,
  IErpDocumentSyncResponse,
  IErpDocumentListItem,
  IErpDocumentProductItem,
} from 'x/types'
// import CONS from 'x/config/constants'

import api from './api'

// ============ MOCK DATA CONFIG ============
// Set to true to use mock data for testing (backend not ready)
const USE_MOCK_DATA = true

// Generate mock ERP document list
function generateMockDocumentList(offset: number, limit: number, hasQtyAdjustment: boolean, searchText?: string): IErpDocumentListResponse {
  const totalMockDocs = hasQtyAdjustment ? 8 : 25
  const filteredTotal = searchText ? Math.min(3, totalMockDocs) : totalMockDocs

  const startIdx = offset
  const endIdx = Math.min(offset + limit, filteredTotal)
  const documents: IErpDocumentListItem[] = []

  for (let i = startIdx; i < endIdx; i++) {
    const docNum = i + 1
    const docType = i % 2 === 0 ? 21 : 31 // alternate between ใบสั่งซื้อ and บันทึกซื้อสินค้า
    const dayOffset = Math.floor(i / 2)
    const issuedDate = new Date()
    issuedDate.setDate(issuedDate.getDate() - dayOffset)

    const doc: IErpDocumentListItem = {
      erp_doc_id: 1000 + docNum,
      erp_doc_code: `RT${issuedDate.getFullYear()}${String(issuedDate.getMonth() + 1).padStart(2, '0')}${String(docNum).padStart(4, '0')}`,
      erp_doc_type: docType,
      issued_date: issuedDate.toISOString().split('T')[0],
      net_amount: Math.floor(Math.random() * 50000) + 1000,
      sum_qty: Math.floor(Math.random() * 100) + 10,
      sum_pp_count: Math.floor(Math.random() * 10) + 1,
      updated_at: issuedDate.toISOString(),
      qty_adjustment_histories: hasQtyAdjustment
        ? [
            {
              id: 5000 + docNum,
              created_at: new Date().toISOString(),
            },
          ]
        : undefined,
    }
    documents.push(doc)
  }

  return {
    documents,
    total_count: filteredTotal,
    has_next_page: endIdx < filteredTotal,
  }
}

// Generate mock products from document
function generateMockProducts(erpDocId: number): IErpDocumentProductsResponse {
  const numProducts = Math.floor(Math.random() * 5) + 2
  const products: IErpDocumentProductItem[] = []

  for (let i = 0; i < numProducts; i++) {
    products.push({
      pp_id: 100000 + erpDocId * 10 + i,
      product_code: `SKU-${erpDocId}-${String(i + 1).padStart(3, '0')}`,
      product_name: `สินค้าทดสอบ ${erpDocId}-${i + 1}`,
      qty: Math.floor(Math.random() * 20) + 1,
      unit_price: Math.floor(Math.random() * 1000) + 100,
    })
  }

  return { products }
}
// ============ END MOCK DATA CONFIG ============

type ICreateErpDocumentRequestBody = {
  store_id: number
  erp_ch_id: number
  order_ids: number[]
  erp_doc_type: number
  is_tax_invoice?: boolean
}

// https://dd.xselly.com/doc/create-document-api-JkqfVcekHX
export async function fetchCreateErpDocument(params: ICreateErpDocumentRequestBody): Promise<CreateErpDocumentResponse> {
  const { store_id, erp_ch_id, order_ids, erp_doc_type, is_tax_invoice } = params
  if (!store_id) {
    throw new Error('fetchCreateErpDocument:: Need store_id')
  }

  if (!erp_ch_id) {
    throw new Error('fetchCreateErpDocument:: Need erp_ch_id')
  }

  if (!order_ids) {
    throw new Error('fetchCreateErpDocument:: Need order_ids')
  }

  if (!erp_doc_type) {
    throw new Error('fetchCreateErpDocument:: Need erp_doc_type')
  }

  const apiOptions: IApiOptions = {
    showSpinner: true,
    messages: {
      successMsg: 'สร้างเอกสารสำเร็จ',
      errorMsg: 'สร้างเอกสารใช้ล้มเหลว กรุณาลองกดใหม่อีกครั้ง',
    },
    axiosOptions: {
      timeout: 60000,
    },
    isApiV2: true,
  }

  const reqBody: ICreateErpDocumentRequestBody = {
    store_id,
    erp_ch_id,
    order_ids,
    erp_doc_type,
  }

  if (is_tax_invoice) {
    reqBody.is_tax_invoice = is_tax_invoice
  }

  try {
    const res = await api.post<ICreateErpDocumentRequestBody, CreateErpDocumentResponse>('erp/document/create', reqBody, apiOptions)
    return res
  } catch (err) {
    console.log('err => ', err)
    throw err
  }
}

/**
 * ดึงรายการเอกสาร ERP สำหรับเติมสต๊อก
 * API: /erp/document/list
 */
export async function fetchErpDocumentList(params: IErpDocumentListRequestBody): Promise<IErpDocumentListResponse> {
  const { store_id, erp_ch_id } = params
  if (!store_id) {
    throw new Error('fetchErpDocumentList:: Need store_id')
  }
  if (!erp_ch_id) {
    throw new Error('fetchErpDocumentList:: Need erp_ch_id')
  }

  const apiOptions: IApiOptions = {
    messages: {
      errorMsg: 'ไม่สามารถดึงรายการเอกสารได้ กรุณาลองใหม่อีกครั้ง',
    },
    axiosOptions: {
      timeout: 30000,
    },
    isApiV2: true,
  }

  const reqBody: IErpDocumentListRequestBody = {
    store_id,
    erp_ch_id,
    offset: params.offset ?? 0,
    limit: params.limit ?? 20,
    sort_by: params.sort_by ?? 'created_at',
    sort_type: params.sort_type ?? 'desc',
  }

  if (params.erp_doc_types && params.erp_doc_types.length > 0) {
    reqBody.erp_doc_types = params.erp_doc_types
  }
  if (params.erp_doc_code) {
    reqBody.erp_doc_code = params.erp_doc_code
  }
  if (params.created_at_from) {
    reqBody.created_at_from = params.created_at_from
  }
  if (params.created_at_to) {
    reqBody.created_at_to = params.created_at_to
  }
  if (params.has_qty_adjustment !== undefined) {
    reqBody.has_qty_adjustment = params.has_qty_adjustment
  }

  // Use mock data for testing
  if (USE_MOCK_DATA) {
    await new Promise((resolve) => setTimeout(resolve, 500)) // simulate network delay
    return generateMockDocumentList(params.offset ?? 0, params.limit ?? 20, params.has_qty_adjustment ?? false, params.erp_doc_code)
  }

  try {
    const res = await api.post<IErpDocumentListRequestBody, IErpDocumentListResponse>('erp/document/list', reqBody, apiOptions)
    return res
  } catch (err) {
    console.log('fetchErpDocumentList err => ', err)
    throw err
  }
}

/**
 * ดึงรายการสินค้าจากเอกสาร ERP เพื่อเติมสต๊อก
 * API: /erp/document/products
 */
export async function fetchErpDocumentProducts(params: IErpDocumentProductsRequestBody): Promise<IErpDocumentProductsResponse> {
  const { erp_doc_id, store_id, erp_ch_id } = params
  if (!erp_doc_id) {
    throw new Error('fetchErpDocumentProducts:: Need erp_doc_id')
  }
  if (!store_id) {
    throw new Error('fetchErpDocumentProducts:: Need store_id')
  }
  if (!erp_ch_id) {
    throw new Error('fetchErpDocumentProducts:: Need erp_ch_id')
  }

  const apiOptions: IApiOptions = {
    showSpinner: true,
    messages: {
      errorMsg: 'ไม่สามารถดึงรายการสินค้าจากเอกสารได้ กรุณาลองใหม่อีกครั้ง',
    },
    axiosOptions: {
      timeout: 30000,
    },
    isApiV2: true,
  }

  const reqBody: IErpDocumentProductsRequestBody = {
    erp_doc_id,
    store_id,
    erp_ch_id,
  }

  if (params.product_code_source) {
    reqBody.product_code_source = params.product_code_source
  }

  // Use mock data for testing
  if (USE_MOCK_DATA) {
    await new Promise((resolve) => setTimeout(resolve, 300)) // simulate network delay
    return generateMockProducts(erp_doc_id)
  }

  try {
    const res = await api.post<IErpDocumentProductsRequestBody, IErpDocumentProductsResponse>('erp/document/products', reqBody, apiOptions)
    return res
  } catch (err) {
    console.log('fetchErpDocumentProducts err => ', err)
    throw err
  }
}

/**
 * ซิงก์เอกสาร ERP
 * API: /erp/document/sync (mock - รอ backend implement)
 */
export async function syncErpDocuments(params: IErpDocumentSyncRequestBody): Promise<IErpDocumentSyncResponse> {
  const { store_id, erp_ch_id } = params
  if (!store_id) {
    throw new Error('syncErpDocuments:: Need store_id')
  }
  if (!erp_ch_id) {
    throw new Error('syncErpDocuments:: Need erp_ch_id')
  }

  const apiOptions: IApiOptions = {
    messages: {
      errorMsg: 'ไม่สามารถซิงก์เอกสารได้ กรุณาลองใหม่อีกครั้ง',
    },
    axiosOptions: {
      timeout: 60000,
    },
    isApiV2: true,
  }

  const reqBody: IErpDocumentSyncRequestBody = {
    store_id,
    erp_ch_id,
  }

  if (params.created_at_from) {
    reqBody.created_at_from = params.created_at_from
  }
  if (params.created_at_to) {
    reqBody.created_at_to = params.created_at_to
  }
  if (params.erp_doc_types && params.erp_doc_types.length > 0) {
    reqBody.erp_doc_types = params.erp_doc_types
  }

  // Use mock data for testing
  if (USE_MOCK_DATA) {
    await new Promise((resolve) => setTimeout(resolve, 800)) // simulate network delay
    return {
      last_synced_at: new Date().toISOString(),
      synced_count: Math.floor(Math.random() * 10) + 1,
      bgjob_uuid: `mock-bgjob-${Date.now()}`,
    }
  }

  try {
    const res = await api.post<IErpDocumentSyncRequestBody, IErpDocumentSyncResponse>('erp/document/sync', reqBody, apiOptions)
    return res
  } catch (err) {
    console.log('syncErpDocuments err => ', err)
    throw err
  }
}
